import React, { useEffect, useState } from 'react';
import { Loader2, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import { log } from '../../lib/logger';

const ReorderRemindersCard = () => {
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/reorder-reminders/preview');
      setPreview(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load reorder reminder preview');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPreview(); }, []);

  const runReminders = async () => {
    if (!preview || preview.count === 0) return;
    if (!window.confirm(`Send a reorder SMS to ${preview.count} customer${preview.count === 1 ? '' : 's'}? They will be charged against your Twilio balance.`)) return;
    setSending(true);
    try {
      const res = await api.post('/admin/reorder-reminders/run');
      setLastRun(res.data);
      toast.success(`Sent ${res.data.sent} reminder${res.data.sent === 1 ? '' : 's'}${res.data.failed ? ` · ${res.data.failed} failed` : ''}`);
      await loadPreview();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to send reminders';
      toast.error(msg);
      log.warn('Reorder reminders run failed', err);
    } finally { setSending(false); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="reorder-reminders-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#0a0a0a]">Monthly reorder SMS</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            Nudges customers whose last paid order was 25–35 days ago. Acts as a subscription-replacement since PayID can't do direct debit. Idempotent — each customer is reminded at most once per cycle.
          </p>
        </div>
        <button
          type="button"
          onClick={loadPreview}
          className="text-xs text-slate-500 hover:text-red-600 underline"
          data-testid="reorder-refresh-btn"
        >Refresh</button>
      </div>

      {loading ? (
        <div className="mt-4 py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : preview && (
        <>
          {!preview.sms_enabled && (
            <div className="mt-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="reorder-sms-disabled-banner">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <b>SMS sending isn't configured.</b> Add either <code className="bg-white px-1 rounded">TWILIO_MESSAGING_SERVICE_SID</code> or <code className="bg-white px-1 rounded">TWILIO_FROM_NUMBER</code> to <code className="bg-white px-1 rounded">/app/backend/.env</code> and restart the backend.
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Eligible" value={preview.count} testid="reorder-eligible-count" />
            <Stat label="Window" value={`${preview.window_days[0]}–${preview.window_days[1]}d`} />
            <Stat label="SMS ready" value={preview.sms_enabled ? 'Yes' : 'No'} valueClass={preview.sms_enabled ? 'text-emerald-600' : 'text-amber-600'} />
            <Stat label="Last batch" value={lastRun ? `${lastRun.sent} sent` : '—'} />
          </div>

          {preview.candidates.length > 0 && (
            <div className="mt-4 border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-50">Candidates</div>
              <ul className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
                {preview.candidates.map((c) => (
                  <li key={c.customer_id} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium text-[#0a0a0a]">{c.customer_name || '(no name)'}</div>
                      <div className="text-xs text-slate-500">{c.phone_masked} · last order {c.last_order_ref}</div>
                    </div>
                    <div className="text-xs text-slate-500">{c.days_ago}d ago</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={runReminders}
              disabled={!preview.sms_enabled || preview.count === 0 || sending}
              data-testid="reorder-send-btn"
              className="inline-flex items-center gap-2 px-5 py-2 bg-red-500 hover:bg-red-400 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold rounded-lg"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? 'Sending…' : preview.count === 0 ? 'No one to remind' : `Send ${preview.count} reminder${preview.count === 1 ? '' : 's'}`}
            </button>
            {lastRun && lastRun.sent > 0 && (
              <span className="text-xs text-emerald-700 inline-flex items-center gap-1.5" data-testid="reorder-last-run">
                <CheckCircle2 size={12} /> {lastRun.sent} sent in last batch
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
};

const Stat = ({ label, value, valueClass = 'text-[#0a0a0a]', testid }) => (
  <div className="bg-slate-50 border border-slate-100 rounded-lg p-3" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-lg font-bold mt-0.5 ${valueClass}`}>{value}</div>
  </div>
);

export default ReorderRemindersCard;
